/**
 * @file src/local/localLibraryStore.js
 * @description What the library remembers about a user's own files — and only that.
 *
 * The bytes are never here — they are in the private file system (`localByteVault.js`) or, in the
 * desktop shell, still in the file the user has. What this store holds is the metadata that makes
 * a file usable the next time: name, size, duration, peaks, loudness gain, the orbiter and world it
 * plays with, where it last lived, and whether this browser took its copy (`copied`). It also keeps
 * whatever the platform offers as a way back to the original — a `FileSystemFileHandle`
 * (structured-cloneable, so it survives here) or a path on the desktop shell.
 *
 *   `entries`   one row per file, keyed by its `local:` id
 *   `meta`      per-collection rail arrangement (order + the stages local files hold), the last
 *               known name of each collection, and flags
 *
 * Same open discipline as the offline store (a bounded open, a repair-by-delete for a database
 * left half-upgraded) — an IndexedDB open that never answers must not take boot with it.
 *
 * ## One file, many collections
 *
 * An entry remembers every collection it belongs to, in `scopes`. Version 1 held a single `scope`,
 * which meant dropping the same file into a second collection minted a second entry and a second
 * copy of the bytes; version 2 rewrites that field into a one-element set. Rows are normalised on
 * read as well as migrated on upgrade, because a database that answered from a tab still holding a
 * version-1 connection would otherwise hand out a shape the rest of the library no longer speaks.
 */

const DB_NAME = 'orbiters-local-library';
const DB_VERSION = 2;
const ENTRIES_STORE = 'entries';
const META_STORE = 'meta';
const OPEN_TIMEOUT_MS = 3000;

/** What analysis has produced for an entry so far. */
export const ANALYSIS_STATE = {
  PENDING: 'pending',
  COARSE: 'coarse',
  READY: 'ready',
  FAILED: 'failed',
};

let dbPromise = null;
let queue = Promise.resolve();
/** True once an open has answered with a database this session; false while it has not. */
let storeAnswered = false;

function serialize(work) {
  const settled = queue.then(work, work);
  queue = settled.then(
    () => undefined,
    () => undefined,
  );
  return settled;
}

function getIndexedDbFactory() {
  return typeof globalThis !== 'undefined' && globalThis.indexedDB ? globalThis.indexedDB : null;
}

const REQUIRED_STORES = [ENTRIES_STORE, META_STORE];

function createStores(db) {
  if (!db.objectStoreNames.contains(ENTRIES_STORE)) {
    db.createObjectStore(ENTRIES_STORE, { keyPath: 'id' });
  }
  if (!db.objectStoreNames.contains(META_STORE)) {
    db.createObjectStore(META_STORE, { keyPath: 'key' });
  }
}

/**
 * A row as the rest of the library reads it: `scopes` is always an array, and the version-1
 * `scope` field is gone. Applied on every read, not only after an upgrade — a row can also arrive
 * from a write this session made before the migration ran, and one field with two spellings is how
 * a filter starts silently missing rows.
 */
export function normalizeLocalEntry(row) {
  if (!row || typeof row !== 'object') return row;
  if (Array.isArray(row.scopes) && !('scope' in row)) return row;
  const scopes = Array.isArray(row.scopes)
    ? row.scopes.filter((scope) => typeof scope === 'string' && scope)
    : typeof row.scope === 'string' && row.scope
      ? [row.scope]
      : [];
  const next = { ...row, scopes: [...new Set(scopes)] };
  delete next.scope;
  return next;
}

/**
 * Rewrite every version-1 row into the `scopes` shape, inside the upgrade transaction so no reader
 * ever sees the database half-converted. A cursor rather than a getAll/put pass: the entries hold
 * `FileSystemFileHandle`s, and reading them all into memory to write them back would be the one
 * moment a large library could exhaust a phone.
 */
function migrateScopesToSets(tx) {
  let store;
  try {
    store = tx.objectStore(ENTRIES_STORE);
  } catch {
    // The store is being created in this same upgrade: there is nothing of version 1 to convert.
    return;
  }
  const cursorRequest = store.openCursor();
  cursorRequest.onsuccess = () => {
    const cursor = cursorRequest.result;
    if (!cursor) return;
    const row = cursor.value;
    if (row && (!Array.isArray(row.scopes) || 'scope' in row)) {
      // A row that will not write must not abort the upgrade and take the whole library with it.
      // An unhandled request error bubbles to the transaction, so it is stopped here; the
      // read-time normalize above still gives this one row the right shape.
      const put = cursor.update(normalizeLocalEntry(row));
      put.onerror = (event) => {
        event.preventDefault();
        console.warn('[local] could not migrate an entry to the multi-collection shape', row?.id);
      };
    }
    cursor.continue();
  };
}

function hasEveryStore(db) {
  return REQUIRED_STORES.every((name) => db.objectStoreNames.contains(name));
}

function deleteDatabase() {
  return new Promise((resolve) => {
    let request;
    try {
      request = getIndexedDbFactory().deleteDatabase(DB_NAME);
    } catch {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => resolve(false), OPEN_TIMEOUT_MS);
    request.onsuccess = () => {
      clearTimeout(timer);
      resolve(true);
    };
    request.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    request.onblocked = () => {
      clearTimeout(timer);
      resolve(false);
    };
  });
}

function openOnce() {
  const factory = getIndexedDbFactory();
  if (!factory) return Promise.resolve(null);
  return new Promise((resolve) => {
    let request;
    try {
      request = factory.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    request.onupgradeneeded = (event) => {
      createStores(request.result);
      // `oldVersion` 0 is a database that did not exist: nothing to convert, and the stores were
      // just made empty. Anything from 1 upwards may hold single-`scope` rows.
      if (event.oldVersion >= 1 && request.transaction) migrateScopesToSets(request.transaction);
    };
    request.onsuccess = () => {
      const db = request.result;
      if (settled) {
        // Answered after the timeout: nobody is waiting, and an untracked connection would block
        // the next upgrade or repair.
        db.close();
        return;
      }
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      finish(db);
    };
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });
}

async function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = (async () => {
    let db = await openOnce();
    if (db && !hasEveryStore(db)) {
      db.close();
      await deleteDatabase();
      db = await openOnce();
      if (db && !hasEveryStore(db)) {
        db.close();
        db = null;
      }
    }
    if (!db) dbPromise = null;
    else storeAnswered = true;
    return db;
  })();
  return dbPromise;
}

function request(store, mode, work) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve, reject) => {
      let tx;
      try {
        tx = db.transaction(store, mode);
      } catch (error) {
        reject(error);
        return;
      }
      let result;
      try {
        result = work(tx.objectStore(store));
      } catch (error) {
        reject(error);
        return;
      }
      tx.oncomplete = () => resolve(result?.result ?? null);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  });
}

/** @typedef {object} LocalEntry
 *  @property {string} id `local:<uuid>`
 *  @property {string} name the file's name as dropped
 *  @property {number} size bytes
 *  @property {number|null} lastModified
 *  @property {string|null} mimeType
 *  @property {number|null} durationSec
 *  @property {object|null} peaks audiowaveform v2 JSON
 *  @property {object|null} loudness `{ integratedLufs, truePeakDb, playbackGainDb, maxAllowableGainDb }`
 *  @property {number|null} [tempoBpm] the detected tempo, or `null` when the analysis found no pulse
 *    steady enough to warp to. The two are different answers and both are stored. ABSENT (rather
 *    than null) means the row predates tempo detection and was never asked — those rows keep the
 *    behaviour they had, which is a track with no grid, until something re-analyses the file.
 *  @property {string} analysis one of ANALYSIS_STATE
 *  @property {{ title: string|null, artist: string|null, album: string|null }} [tags] what the file
 *    says about itself, read out of it on import. Absent for a file that says nothing, and for
 *    every row written before this was read at all.
 *  @property {string[]} scopes every collection (or inline session) this file was added to. Empty
 *    means it belongs to none — the file is still here, and the local files page is where it lives.
 *  @property {boolean} [copied] whether this browser holds its own copy of the bytes
 *  @property {FileSystemFileHandle|null} handle Chrome's way back to the file
 *  @property {string|null} path the desktop shell's way back to the file
 *  @property {string|null} folder the last known folder, for display and for "Search a folder"
 *  @property {string|null} orbiterId
 *  @property {string|null} orbiterVersion
 *  @property {string|null} entangledWorldId
 *  @property {string|null} entangledWorldVersion
 *  @property {number} addedAt
 */

export async function listLocalEntries() {
  const rows = await request(ENTRIES_STORE, 'readonly', (store) => store.getAll());
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeLocalEntry).sort((a, b) => (a.addedAt ?? 0) - (b.addedAt ?? 0));
}

export async function readLocalEntry(id) {
  if (!id) return null;
  return normalizeLocalEntry(await request(ENTRIES_STORE, 'readonly', (store) => store.get(id)));
}

export async function writeLocalEntry(entry) {
  if (!entry?.id) return null;
  return serialize(async () => {
    if (!(await openDb())) return null;
    await request(ENTRIES_STORE, 'readwrite', (store) => store.put(entry));
    return entry;
  });
}

/** Merge `patch` into the stored row; a row that vanished meanwhile is not resurrected. */
export async function patchLocalEntry(id, patch) {
  if (!id) return null;
  return serialize(async () => {
    const current = await request(ENTRIES_STORE, 'readonly', (store) => store.get(id));
    if (!current) return null;
    const next = normalizeLocalEntry({ ...current, ...patch });
    await request(ENTRIES_STORE, 'readwrite', (store) => store.put(next));
    return next;
  });
}

export async function deleteLocalEntry(id) {
  if (!id) return false;
  return serialize(() => request(ENTRIES_STORE, 'readwrite', (store) => store.delete(id)).then(() => true));
}

/**
 * Where a collection's local files sit: their order on the rail, and the stages they were left on.
 *
 * ONE record for both, under the key the order alone used to hold. They describe a single
 * arrangement — an id in one and not the other is a contradiction, not a state — and two rows would
 * be two chances to drift apart. Nothing migrates: a row written before stages were remembered
 * simply has no `stages`, and reads as an arrangement with no placements.
 *
 * `stages` is keyed by stage INDEX rather than being a positional array, because this record only
 * ever speaks for the local cards. The stages a hosted track holds are the collection's layout's to
 * describe, and a placement here never claims one (see `stageIsFree` on the controller).
 *
 * `swaps` is keyed by stage index too: a stage is a TRIAD (a track, an orbiter and a world), and a
 * local file can have an orbiter or world swapped onto its stage exactly like a hosted track can.
 * Stored beside the placement rather than in the collection's layout for the same reason the
 * placement is — the stage a `local:` id holds is blanked on the way out of there. Nothing migrates:
 * a row written before this simply has no `swaps` and reads as no stage having one.
 *
 * `panel` is the local-files PANEL itself: whether it was left showing, and the width it was dragged
 * to. It lives here rather than in the collection's layout because the panel shows THIS machine's
 * files — a viewer opening someone else's collection was otherwise handed the editor's open panel
 * and an empty list of files they do not have. The panel's state is a fact about the same per-machine
 * thing its contents are, so it is kept with them. Nothing migrates: a row written before this has
 * no `panel` and reads as a panel nobody has opened, which is the default anyway.
 *
 * @typedef {{ ids: string[], stages: Record<string, string>,
 *   swaps: Record<string, {orbiterId?: string, entangledWorldId?: string}>,
 *   panel: {open?: boolean, width?: number} }} LocalRailArrangement
 */
export async function readLocalRailArrangement(scopeKey) {
  const row = await request(META_STORE, 'readonly', (store) => store.get(`order:${scopeKey || 'default'}`));
  return {
    ids: Array.isArray(row?.ids) ? row.ids : [],
    stages: row?.stages && typeof row.stages === 'object' ? { ...row.stages } : {},
    swaps: row?.swaps && typeof row.swaps === 'object' ? { ...row.swaps } : {},
    panel: row?.panel && typeof row.panel === 'object' ? { ...row.panel } : {},
  };
}

/**
 * Change SOME fields of one scope's row and keep the rest exactly as stored.
 *
 * The read and the write happen inside ONE readwrite transaction, which IndexedDB serialises against
 * every other tab's transaction on this store. That is the point of it: the row has two independent
 * owners — the rail arrangement and the local-files panel — and each must be able to write its own
 * part without carrying a copy of the other's. A tab that merely toggles the panel used to write its
 * whole in-memory snapshot of the rail too, putting back placements another tab had since changed.
 */
function patchRailRow(scopeKey, fields) {
  const key = `order:${scopeKey || 'default'}`;
  return serialize(() =>
    request(META_STORE, 'readwrite', (store) => {
      const read = store.get(key);
      read.onsuccess = () => {
        const row = read.result && typeof read.result === 'object' ? read.result : {};
        store.put({ ...row, ...fields, key });
      };
      return read;
    }),
  );
}

/** The rail's own part of the row. The panel's state is left exactly as stored. */
export async function writeLocalRailArrangement(scopeKey, arrangement) {
  const { ids, stages, swaps } = arrangement ?? {};
  return patchRailRow(scopeKey, {
    ids: Array.isArray(ids) ? ids : [],
    stages: stages && typeof stages === 'object' ? { ...stages } : {},
    swaps: swaps && typeof swaps === 'object' ? { ...swaps } : {},
  });
}

/** The local-files panel's part of the row. The rail and its placements are left exactly as stored. */
export async function writeLocalRailPanel(scopeKey, panel) {
  return patchRailRow(scopeKey, { panel: panel && typeof panel === 'object' ? { ...panel } : {} });
}

/**
 * The name a collection had the last time a file was added to it.
 *
 * Kept here rather than fetched, because the page that needs it must be able to name a collection
 * that no longer exists — a deleted collection is precisely the case where its files have nowhere
 * else to be seen — and because naming them should not cost one request per collection.
 */
export async function writeLocalScopeName(scopeKey, name) {
  if (!scopeKey || typeof name !== 'string' || !name.trim()) return null;
  return serialize(() =>
    request(META_STORE, 'readwrite', (store) => store.put({ key: `scope:${scopeKey}`, name: name.trim() })),
  );
}

/** scope key → last known name, for every collection this library has seen. */
export async function readLocalScopeNames() {
  const rows = await request(META_STORE, 'readonly', (store) => store.getAll());
  const names = {};
  if (!Array.isArray(rows)) return names;
  for (const row of rows) {
    if (typeof row?.key === 'string' && row.key.startsWith('scope:') && row.name) {
      names[row.key.slice('scope:'.length)] = row.name;
    }
  }
  return names;
}

/**
 * A once-per-browser fact about the library itself — today only "the copy has been explained".
 * It lives in the meta store beside the rail order, with the library's own data, so forgetting the
 * library forgets it too and the explanation is given again to a browser starting over.
 */
export async function readLocalFlag(name) {
  const row = await request(META_STORE, 'readonly', (store) => store.get(`flag:${name}`));
  return Boolean(row?.value);
}

export async function writeLocalFlag(name, value) {
  return serialize(() =>
    request(META_STORE, 'readwrite', (store) => store.put({ key: `flag:${name}`, value: Boolean(value) })),
  );
}

export async function clearLocalLibrary() {
  return serialize(async () => {
    await request(ENTRIES_STORE, 'readwrite', (store) => store.clear());
    await request(META_STORE, 'readwrite', (store) => store.clear());
    return true;
  });
}

/** Whether the store has opened this session. A reader that got `[]` while this is false saw an
 *  open that did not answer, not an empty library. */
export function isLocalStoreOpen() {
  return storeAnswered;
}

/** Tests only. */
export function resetLocalLibraryStoreForTests() {
  dbPromise = null;
  queue = Promise.resolve();
  storeAnswered = false;
}
